import { useState, useRef } from 'react';
import { useStore } from './store';
import { t, type Lang, formatMoney } from './i18n';
import { parseJalali, formatJalali, jalaliDaysInMonth, jalaliFirstWeekday, jalaliMonths, jalaliWeekdaysFa, jalaliWeekdaysEn, todayJalali } from './jalali';
import type { EntityType, Side, FlowNode, Entity } from './types';

function nodeLabel(node: FlowNode, entities: Entity[]): string {
  let sub = '';
  if (node.metadataJson) { try { sub = JSON.parse(node.metadataJson).subLabel || ''; } catch { /* ignore */ } }
  const name = entities.find((e) => e.id === node.entityId)?.name || '?';
  return sub ? `${sub} — ${name}` : name;
}

function formatGrouped(intStr: string, lang: Lang): string {
  if (!intStr) return '';
  const n = parseInt(intStr, 10);
  if (isNaN(n)) return '';
  return n.toLocaleString(lang === 'fa' ? 'fa-IR' : 'en-US');
}

function AmountField({ label, value, onCommit, lang, autoFocus }: { label: string; value: number; onCommit: (n: number) => void; lang: Lang; autoFocus?: boolean }) {
  const [display, setDisplay] = useState(value > 0 ? formatGrouped(String(Math.round(value)), lang) : '');
  const handle = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    const intPart = parts[0].replace(/\D/g, '');
    const dec = parts[1] !== undefined ? '.' + parts[1].replace(/\D/g, '').slice(0, 2) : '';
    const digits = intPart + dec;
    const n = digits === '' ? 0 : parseFloat(digits);
    setDisplay(intPart === '' ? '' : formatGrouped(intPart, lang) + dec);
    onCommit(n);
  };
  return (
    <label className="fld">{label}
      <input inputMode="numeric" value={display} autoFocus={autoFocus} onChange={(e) => handle(e.target.value)} />
    </label>
  );
}

function Modal({ title, onClose, children, corner }: { title: string; onClose: () => void; children: React.ReactNode; corner?: boolean }) {
  const lang = useStore((s) => s.lang);
  return (
    <div className={`overlay ${corner ? 'corner' : ''}`} onClick={onClose}>
      <div className="modal" dir={lang === 'fa' ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span>{title}</span>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function EntityModal({ onClose }: { onClose: () => void }) {
  const lang = useStore((s) => s.lang);
  const addEntity = useStore((s) => s.addEntity);
  const [name, setName] = useState('');
  const [type, setType] = useState<EntityType>('customer');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await addEntity(type, name.trim()); onClose(); } catch (e) { alert((e as Error).message); setBusy(false); }
  };
  return (
    <Modal title={t('addEntity', lang)} onClose={onClose}>
      <label className="fld">{t('type', lang)}
        <select value={type} onChange={(e) => setType(e.target.value as EntityType)}>
          <option value="customer">{t('customer', lang)}</option>
          <option value="bank">{t('bank', lang)}</option>
        </select>
      </label>
      <label className="fld">{t('name', lang)}
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <div className="modal-actions">
        <button className="btn" onClick={onClose}>{t('cancel', lang)}</button>
        <button className="btn primary" disabled={busy} onClick={save}>{t('save', lang)}</button>
      </div>
    </Modal>
  );
}

function EntityPicker({ entities, value, onChange, lang }: { entities: Entity[]; value: string; onChange: (id: string) => void; lang: Lang }) {
  const [q, setQ] = useState('');
  const filtered = entities.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()) || e.type.includes(q.toLowerCase()));
  return (
    <div className="ent-picker">
      <input className="ent-search" placeholder={t('search', lang)} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="ent-opts">
        {filtered.length === 0 && <div className="muted">—</div>}
        {filtered.map((e) => (
          <button key={e.id} type="button" className={`ent-opt ${e.id === value ? 'sel' : ''}`} onClick={() => onChange(e.id)}>
            {e.name} <em>({e.type})</em>
          </button>
        ))}
      </div>
    </div>
  );
}

function NodePicker({ nodes, value, onChange, lang, placeholder }: { nodes: FlowNode[]; value: string; onChange: (id: string) => void; lang: Lang; placeholder?: string }) {
  const [open, setOpen] = useState(false);
  const allocations = useStore((s) => s.allocations);
  const currency = useStore((s) => s.currency);
  const entities = useStore((s) => s.entities);
  const remainingOf = (n: FlowNode) => {
    const used = allocations.filter((a) => (n.side === 'payer' ? a.sourceNodeId === n.id : a.targetNodeId === n.id)).reduce((s, a) => s + a.amount, 0);
    return (n.totalAmount || 0) - used;
  };
  const sel = nodes.find((n) => n.id === value);
  return (
    <div className="combo">
      <button type="button" className="combo-btn" onClick={() => setOpen((o) => !o)}>
        {sel ? nodeLabel(sel, entities) : (placeholder ?? t('selectEntity', lang))}
      </button>
      {open && (
        <>
          <div className="combo-backdrop" onClick={() => setOpen(false)} />
          <div className="combo-pop">
            {nodes.length === 0 && <div className="muted" style={{ padding: 8 }}>—</div>}
            {nodes.map((n) => {
              const rem = remainingOf(n);
              const full = rem <= 0;
              return (
                <button key={n.id} type="button" disabled={full} className={`ent-opt ${full ? 'full' : ''}`} onClick={() => { if (!full) { onChange(n.id); setOpen(false); } }}>
                  <span>{nodeLabel(n, entities)}</span>
                  <em>{full ? t('fullyAllocated', lang) : formatMoney(rem, currency, lang)}</em>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function NodeModal({ side, onClose }: { side: Side; onClose: () => void }) {
  const lang = useStore((s) => s.lang);
  const entities = useStore((s) => s.entities);
  const addNode = useStore((s) => s.addNode);
  const [entityId, setEntityId] = useState(entities[0]?.id ?? '');
  const [amount, setAmount] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!entityId) return alert('select entity');
    if (!(amount >= 0)) return alert('amount required');
    setBusy(true);
    try { await addNode(side, entityId, amount); onClose(); } catch (e) { alert((e as Error).message); setBusy(false); }
  };
  return (
    <Modal title={side === 'payer' ? t('addPayer', lang) : t('addReceiver', lang)} onClose={onClose}>
      <label className="fld">{t('selectEntity', lang)}
        <EntityPicker entities={entities} value={entityId} onChange={setEntityId} lang={lang} />
      </label>
      <AmountField label={t('totalAmount', lang)} value={amount} onCommit={setAmount} lang={lang} autoFocus />
      <div className="modal-actions">
        <button className="btn" onClick={onClose}>{t('cancel', lang)}</button>
        <button className="btn primary" disabled={busy} onClick={save}>{t('save', lang)}</button>
      </div>
    </Modal>
  );
}

export function AllocateModal({ preselect, onClose }: { preselect?: FlowNode; onClose: () => void }) {
  const lang = useStore((s) => s.lang);
  const currency = useStore((s) => s.currency);
  const nodes = useStore((s) => s.nodes);
  const entities = useStore((s) => s.entities);
  const allocations = useStore((s) => s.allocations);
  const addAllocation = useStore((s) => s.addAllocation);

  const payers = nodes.filter((n) => n.side === 'payer');
  const receivers = nodes.filter((n) => n.side === 'receiver');
  const [source, setSource] = useState(preselect?.side === 'payer' ? preselect.id : payers[0]?.id ?? '');
  const [target, setTarget] = useState(preselect?.side === 'receiver' ? preselect.id : receivers[0]?.id ?? '');
  const [amount, setAmount] = useState<number>(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const srcNode = payers.find((p) => p.id === source);
  const allocated = allocations.filter((a) => a.sourceNodeId === source).reduce((s, a) => s + a.amount, 0);
  const remaining = (srcNode?.totalAmount ?? 0) - allocated;

  const save = async () => {
    setErr(null);
    const amt = amount;
    if (!source || !target) return setErr('select both');
    if (!(amt > 0)) return setErr('amount > 0');
    if (amt > remaining + 1e-9) return setErr(t('overAlloc', lang));
    setBusy(true);
    try { await addAllocation(source, target, amt, note.trim() || undefined); onClose(); } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  return (
    <Modal title={t('allocate', lang)} onClose={onClose}>
      <label className="fld">{t('payer', lang)}
        <NodePicker nodes={payers} value={source} onChange={setSource} lang={lang} />
      </label>
      <label className="fld">{t('target', lang)}
        <NodePicker nodes={receivers} value={target} onChange={setTarget} lang={lang} />
      </label>
      <div className="remaining">{t('remaining', lang)}: {currency}{Math.round(remaining * 100) / 100}</div>
      <AmountField label={t('amount', lang)} value={amount} onCommit={setAmount} lang={lang} autoFocus />
      <label className="fld">{t('note', lang)}
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {err && <div className="err">{err}</div>}
      <div className="modal-actions">
        <button className="btn" onClick={onClose}>{t('cancel', lang)}</button>
        <button className="btn primary" disabled={busy} onClick={save}>{t('save', lang)}</button>
      </div>
    </Modal>
  );
}

export function EditNodeModal({ node, onClose }: { node: FlowNode; onClose: () => void }) {
  const lang = useStore((s) => s.lang);
  const entities = useStore((s) => s.entities);
  const updateNodeData = useStore((s) => s.updateNodeData);
  const removeNode = useStore((s) => s.removeNode);
  const [amount, setAmount] = useState<number>(node.totalAmount);
  const [busy, setBusy] = useState(false);
  const [delHold, setDelHold] = useState(false);
  const delTimer = useRef<number | null>(null);
  const save = async () => {
    if (!(amount >= 0)) return alert('amount required');
    setBusy(true);
    try { await updateNodeData(node.id, amount); onClose(); } catch (e) { alert((e as Error).message); setBusy(false); }
  };
  const startDel = () => {
    setDelHold(true);
    delTimer.current = window.setTimeout(async () => { setDelHold(false); await removeNode(node.id); onClose(); }, 2000);
  };
  const cancelDel = () => {
    setDelHold(false);
    if (delTimer.current) { clearTimeout(delTimer.current); delTimer.current = null; }
  };
  return (
    <Modal title={t('editNode', lang)} onClose={onClose}>
      <div className="fld" style={{ color: 'var(--ink)', fontWeight: 700 }}>{nodeLabel(node, entities)}</div>
      <AmountField label={t('totalAmount', lang)} value={amount} onCommit={setAmount} lang={lang} autoFocus />
      <div className="modal-actions">
        <button
          className={`btn danger ${delHold ? 'holding' : ''}`}
          title={t('holdToDelete', lang)}
          onPointerDown={(e) => { e.stopPropagation(); startDel(); }}
          onPointerUp={cancelDel}
          onPointerLeave={cancelDel}
        >{delHold ? (lang === 'fa' ? 'در حال حذف…' : 'Deleting…') : t('delete', lang)}</button>
        <button className="btn primary" disabled={busy} onClick={save}>{t('save', lang)}</button>
      </div>
    </Modal>
  );
}

export function NodeDetailModal({ node, onClose, onEdit }: { node: FlowNode; onClose: () => void; onEdit: () => void }) {
  const lang = useStore((s) => s.lang);
  const currency = useStore((s) => s.currency);
  const entities = useStore((s) => s.entities);
  const nodes = useStore((s) => s.nodes);
  const allocations = useStore((s) => s.allocations);
  const addAllocation = useStore((s) => s.addAllocation);
  const removeAllocation = useStore((s) => s.removeAllocation);
  const removeNode = useStore((s) => s.removeNode);
  const isPayer = node.side === 'payer';
  const peers = isPayer ? nodes.filter((n) => n.side === 'receiver') : nodes.filter((n) => n.side === 'payer');
  const initialPeer = (() => {
    for (const p of peers) {
      const pu = allocations.filter((a) => (isPayer ? a.targetNodeId : a.sourceNodeId) === p.id).reduce((s, a) => s + a.amount, 0);
      if ((p.totalAmount || 0) - pu > 0) return p.id;
    }
    return peers[0]?.id ?? '';
  })();
  const [peer, setPeer] = useState(initialPeer);
  const [amt, setAmt] = useState<number>(0);
  const [err, setErr] = useState<string | null>(null);
  const [delHold, setDelHold] = useState(false);
  const delTimer = useRef<number | null>(null);

  const related = allocations.filter((a) => (isPayer ? a.sourceNodeId === node.id : a.targetNodeId === node.id));
  const total = node.totalAmount || 0;
  const used = related.reduce((s, a) => s + a.amount, 0);
  const remaining = total - used;


  const doAllocate = async () => {
    setErr(null);
    if (!peer) return setErr('select peer');
    if (!(amt > 0)) return setErr('amount > 0');
    if (amt > remaining + 1e-9) return setErr(t('overAlloc', lang));
    try { await addAllocation(isPayer ? node.id : peer, isPayer ? peer : node.id, amt); setAmt(0); } catch (e) { setErr((e as Error).message); }
  };
  const startDel = () => {
    setDelHold(true);
    delTimer.current = window.setTimeout(async () => { setDelHold(false); await removeNode(node.id); onClose(); }, 2000);
  };
  const cancelDel = () => {
    setDelHold(false);
    if (delTimer.current) { clearTimeout(delTimer.current); delTimer.current = null; }
  };

  return (
    <Modal title={nodeLabel(node, entities)} onClose={onClose}>
      <div className="remaining">{t('remaining', lang)}: {formatMoney(remaining, currency, lang)}</div>
      <section className="set-sec">
        <h4>{t('allocate', lang)}</h4>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <NodePicker nodes={peers} value={peer} onChange={setPeer} lang={lang} />
          <AmountField label={t('amount', lang)} value={amt} onCommit={setAmt} lang={lang} />
          <button className="btn primary" onClick={doAllocate}>{t('allocate', lang)}</button>
        </div>
        {err && <div className="err">{err}</div>}
      </section>
      <section className="set-sec">
        <h4>{isPayer ? t('allocatedTo', lang) : t('allocatedFrom', lang)}</h4>
        <div className="ent-list">
          {related.length === 0 && <span className="muted">—</span>}
          {related.map((a) => {
            const otherId = isPayer ? a.targetNodeId : a.sourceNodeId;
            const other = nodes.find((n) => n.id === otherId);
            return (
              <div key={a.id} className="ent-row">
                <span>{other ? nodeLabel(other, entities) : '?'}<em>{formatMoney(a.amount, currency, lang)}</em></span>
                <button className="chip-x" title={t('delete', lang)} onClick={() => removeAllocation(a.id)}>✕</button>
              </div>
            );
          })}
        </div>
      </section>
      <div className="modal-actions" style={{ justifyContent: 'flex-start', gap: 10 }}>
        <button className="icon-btn" title={t('editNode', lang)} onClick={onEdit}>✎</button>
        <button className={`icon-btn ${delHold ? 'holding' : ''}`} title={t('holdToDelete', lang)} onPointerDown={(e) => { e.stopPropagation(); startDel(); }} onPointerUp={cancelDel} onPointerLeave={cancelDel}>{delHold ? '…' : '🗑'}</button>
      </div>
    </Modal>
  );
}

export function DatePickerModal({ value, onClose, onSelect }: { value: string; onClose: () => void; onSelect: (s: string) => void }) {
  const lang = useStore((s) => s.lang);
  const p = parseJalali(value) ?? { jy: 1403, jm: 1, jd: 1 };
  const [view, setView] = useState({ y: p.jy, m: p.jm });
  const wd = lang === 'fa' ? jalaliWeekdaysFa : jalaliWeekdaysEn;
  const dim = jalaliDaysInMonth(view.y, view.m);
  const first = jalaliFirstWeekday(view.y, view.m);
  const goMonth = (d: number) => { let m = view.m + d; let y = view.y; if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; } setView({ y, m }); };
  const cells: (number | null)[] = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  return (
    <Modal title={t('date', lang)} onClose={onClose}>
      <div className="row" style={{ justifyContent: 'center' }}>
        <button className="btn icon" onClick={() => goMonth(-1)}>◀</button>
        <span className="dateval" style={{ minWidth: 130, textAlign: 'center' }}>{view.y} {jalaliMonths[view.m - 1]}</span>
        <button className="btn icon" onClick={() => goMonth(1)}>▶</button>
        <button className="btn" onClick={() => { onSelect(todayJalali()); onClose(); }}>{t('today', lang)}</button>
      </div>
      <div className="cal-grid">
        {wd.map((w, i) => <div key={'h' + i} className="cal-h">{w}</div>)}
        {cells.map((d, i) => d === null
          ? <div key={i} className="cal-cell empty" />
          : <button key={i} className={`cal-cell ${d === p.jd && view.m === p.jm && view.y === p.jy ? 'sel' : ''}`} onClick={() => { onSelect(formatJalali(view.y, view.m, d)); onClose(); }}>{d}</button>)}
      </div>
    </Modal>
  );
}

export function SettingsModal({ onClose }: { onClose: () => void }) {
  const lang = useStore((s) => s.lang);
  const theme = useStore((s) => s.theme);
  const currency = useStore((s) => s.currency);
  const entities = useStore((s) => s.entities);
  const addEntity = useStore((s) => s.addEntity);
  const removeEntity = useStore((s) => s.removeEntity);
  const setLang = useStore((s) => s.setLang);
  const setTheme = useStore((s) => s.setTheme);
  const setCurrency = useStore((s) => s.setCurrency);
  const [name, setName] = useState('');
  const [type, setType] = useState<EntityType>('customer');
  const save = async () => {
    if (!name.trim()) return;
    try { await addEntity(type, name.trim()); setName(''); } catch (e) { alert((e as Error).message); }
  };
  return (
    <Modal title={t('settings', lang)} onClose={onClose} corner>
      <section className="set-sec">
        <h4>{t('entities', lang)}</h4>
        <div className="row">
          <select value={type} onChange={(e) => setType(e.target.value as EntityType)}>
            <option value="customer">{t('customer', lang)}</option>
            <option value="bank">{t('bank', lang)}</option>
          </select>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('name', lang)} />
          <button className="btn primary" onClick={save}>{t('addEntity', lang)}</button>
        </div>
        <div className="ent-list">
          {entities.length === 0 && <span className="muted">—</span>}
          {entities.map((e) => (
            <div key={e.id} className="ent-row">
              <span>{e.name} <em>({e.type})</em></span>
              <button className="chip-x" onClick={() => removeEntity(e.id)} title={t('delete', lang)}>✕</button>
            </div>
          ))}
        </div>
      </section>
      <section className="set-sec">
        <h4>{t('language', lang)}</h4>
        <div className="row">
          <button className={`btn ${lang === 'en' ? 'primary' : ''}`} onClick={() => setLang('en')}>EN</button>
          <button className={`btn ${lang === 'fa' ? 'primary' : ''}`} onClick={() => setLang('fa')}>FA</button>
        </div>
      </section>
      <section className="set-sec">
        <h4>{t('theme', lang)}</h4>
        <div className="row">
          <button className={`btn ${theme === 'light' ? 'primary' : ''}`} onClick={() => setTheme('light')}>☀️ {t('light', lang)}</button>
          <button className={`btn ${theme === 'dark' ? 'primary' : ''}`} onClick={() => setTheme('dark')}>🌙 {t('dark', lang)}</button>
        </div>
      </section>
      <section className="set-sec">
        <h4>{t('currency', lang)}</h4>
        <input value={currency} onChange={(e) => setCurrency(e.target.value)} />
      </section>
    </Modal>
  );
}
